/**
 * Services Chart Component
 * Weekly/monthly services visualization with bar chart
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import { TrendingUp, CalendarDays, Info, RefreshCw } from 'lucide-react'
import type { WeeklyServiceData } from '@/types/dashboard'
import { useLocalization } from '@/contexts/LocalizationContext'
import { SAR_SYMBOL } from '@/utils/formatters'
import { cn } from '@/lib/utils'

// ==================== Types ====================

interface ServicesChartProps {
  data: WeeklyServiceData[]
  organizationName?: string
  isWeekly?: boolean
  title?: string
  loading?: boolean
  showInsights?: boolean
  /** When provided, renders a refresh button that refetches the chart data. */
  onRefresh?: () => void
  /** Spins the refresh icon and disables the button while a refetch is in flight. */
  isRefreshing?: boolean
}

// ==================== Colors ====================

// Day-specific colors for visual distinction
const getDayColor = (day: string): string => {
  const dayColors: Record<string, string> = {
    Mon: '#131375', // dark blue
    Tue: '#00A67F', // green
    Wed: '#EF4354', // red
    Thu: '#497BE8', // blue
    Fri: '#E67E22', // orange
    Sat: '#F7B6B3', // pink
    Sun: '#B0E46A', // light green
  }
  return dayColors[day] || '#131375'
}

// ==================== Component ====================

export function ServicesChart({
  data,
  organizationName,
  isWeekly = true,
  title,
  loading = false,
  showInsights = false,
  onRefresh,
  isRefreshing = false,
}: ServicesChartProps) {
  const { t } = useLocalization()
  const chartData = data

  const refreshButton = onRefresh ? (
    <button
      type="button"
      onClick={onRefresh}
      disabled={isRefreshing}
      title={t('common.refresh')}
      aria-label={t('common.refresh')}
      className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 shrink-0 p-1 -m-1"
    >
      <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
    </button>
  ) : null

  // Loading state
  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32 mt-1" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    )
  }

  // Empty state
  if (!chartData || chartData.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">
                {title || `${t('dashboard.weeklyServices')}${organizationName ? ` - ${organizationName}` : ''}`}
              </CardTitle>
            </div>
            {refreshButton}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Info className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="font-medium text-muted-foreground mb-2">{t('dashboard.noDataAvailable')}</h3>
            <p className="text-sm text-muted-foreground max-w-[280px]">
              Service statistics are not currently available. This could be because:
            </p>
            <ul className="text-sm text-muted-foreground mt-2 space-y-1 text-start">
              <li>• No services have been completed this week</li>
              <li>• Data is still being processed</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    )
  }

  const chartTitle = title || 
    (isWeekly
      ? `${t('dashboard.servicesCompletedThisWeek')}${organizationName ? ` - ${organizationName}` : ''}`
      : `${t('dashboard.servicesOverview')}${organizationName ? ` - ${organizationName}` : ''}`)

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">{chartTitle}</CardTitle>
            </div>
            <CardDescription className="mt-1">
              {t('dashboard.completedServicesByDay')}
            </CardDescription>
          </div>
          {refreshButton}
        </div>
      </CardHeader>
      <CardContent>
        {/* Chart - min-w-0 ensures flex/grid gives valid width; explicit height avoids Recharts -1 dimension warning */}
        <div className="h-64 min-h-[256px] min-w-0 w-full">
          <ResponsiveContainer width="100%" height={256}>
            <BarChart 
              data={chartData} 
              margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#e5e7eb" 
                vertical={false}
              />
              <XAxis
                dataKey="day"
                tick={{ fill: '#6b7280', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#6b7280', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
              />
              <Bar 
                dataKey="services" 
                radius={[6, 6, 0, 0]}
                maxBarSize={50}
              >
                {isWeekly &&
                  chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={getDayColor(entry.day)}
                    />
                  ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        {isWeekly && (
          <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-2">
            {chartData.map((entry) => (
              <div key={entry.day} className="flex items-center gap-1.5">
                <div 
                  className="w-3 h-3 rounded-sm" 
                  style={{ backgroundColor: getDayColor(entry.day) }}
                />
                <span className="text-xs text-muted-foreground">{entry.day}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ==================== Custom Tooltip ====================

interface TooltipProps {
  active?: boolean
  payload?: Array<{
    value: number
    payload: WeeklyServiceData
  }>
  label?: string
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  const { t } = useLocalization()
  if (!active || !payload || payload.length === 0) return null

  const data = payload[0].payload

  return (
    <div className="bg-white border border-border rounded-lg shadow-lg p-3 min-w-[140px]">
      <p className="font-medium text-sm mb-2">{data.dayFull || label}</p>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-4">
          <span className="text-xs text-muted-foreground">{t('dashboard.services')}</span>
          <span className="text-sm font-semibold">{data.services}</span>
        </div>
        {data.orders !== undefined && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground">{t('dashboard.orders')}</span>
            <span className="text-sm font-semibold">{data.orders}</span>
          </div>
        )}
        {data.revenue !== undefined && data.revenue > 0 && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground">{t('dashboard.revenue')}</span>
            <span className="text-sm font-semibold">
              {SAR_SYMBOL} {data.revenue.toLocaleString()}
            </span>
          </div>
        )}
        {data.percentage !== undefined && data.percentage > 0 && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground">{t('dashboard.share')}</span>
            <span className="text-sm font-semibold">{data.percentage}%</span>
          </div>
        )}
      </div>
    </div>
  )
}
